/**
 * Ships' own names, per nation and ship class: real ships of each navy, of the class or the
 * nearest one it had, from the age of sail to the first ironclads. The n-th ship of a class
 * (its id's number) takes the n-th name; a list is at least as long as the largest fleet's
 * count of the class (the classic 28: 2 · 4 · 3 · 5 · 2 · 3 · 4 · 1 · 1 · 1 · 1 · 1).
 * Where a navy never had the class, the nearest analogue stands in (noted).
 * Names keep their own spelling in every language.
 */
type Names = Readonly<Record<string, readonly string[]>>;

const SHIP_NAMES: Readonly<Record<string, Names>> = {
  spain: {
    first_rate: ['Santísima Trinidad', 'Príncipe de Asturias', 'Santa Ana'],
    ship_of_line: ['San Juan Nepomuceno', 'Montañés', 'Bahama', 'San Ildefonso', 'Neptuno'],
    sail_frigate: ['Nuestra Señora de las Mercedes', 'Santa Brígida', 'Santa Leocadia', 'Esmeralda'],
    brig: ['Palomo', 'Galgo', 'Volador', 'Vigilante', 'Ligero', 'Animoso'],
    cutter: ['Ardilla', 'Vivo', 'Corzo'],
    galleon: ['Nuestra Señora de Atocha', 'San José', 'Nuestra Señora de Covadonga', 'Santa Margarita'],
    galley: ['Real', 'Capitana', 'Patrona', 'San Felipe', 'Santa Bárbara'],
    ironclad_battery: ['Numancia', 'Tetuán'],
    steam_frigate: ['Villa de Madrid', 'Blanca'],
    steam_corvette: ['Tornado', 'Vencedora'],
    paddle_steamer: ['Isabel II', 'Reina Gobernadora'],
    // A river monitor of 1874: the navy's only one.
    monitor: ['Puigcerdà'],
  },
  france: {
    first_rate: ['Soleil Royal', 'Océan', 'Bretagne'],
    ship_of_line: ['Redoutable', 'Bucentaure', 'Fougueux', 'Intrépide', 'Achille'],
    sail_frigate: ['Hermione', 'Belle Poule', 'Hortense', 'Thémis'],
    brig: ['Mercure', 'Cygne', 'Lutin', 'Railleur', 'Abeille', 'Palinure'],
    cutter: ['Rapide', 'Espiègle', 'Pandour'],
    // The great ships of Louis XIII stand for galleons, which the French navy did not name so.
    galleon: ['La Couronne', 'Saint-Louis', 'Saint-Philippe', 'Vaisseau du Roi'],
    galley: ['La Réale', 'La Patronne', 'La Dauphine', 'La Valeur', 'La Fière'],
    ironclad_battery: ['Dévastation', 'Lave'],
    steam_frigate: ['Pomone', 'Isly'],
    steam_corvette: ['Primauguet', 'Roland'],
    paddle_steamer: ['Sphinx', 'Gomer'],
    monitor: ['Tonnerre', 'Onondaga'],
  },
  usa: {
    first_rate: ['Pennsylvania', 'Independence', 'Franklin'],
    ship_of_line: ['Ohio', 'North Carolina', 'Delaware', 'Columbus', 'Vermont'],
    sail_frigate: ['Constitution', 'United States', 'Constellation', 'President'],
    brig: ['Niagara', 'Lawrence', 'Argus', 'Somers', 'Syren', 'Enterprise'],
    // Revenue cutters.
    cutter: ['Eagle', 'Dallas', 'Massachusetts'],
    // No galleons: the converted merchantmen of the Continental Navy stand in.
    galleon: ['Bonhomme Richard', 'Alfred', 'Alliance', 'Pallas'],
    // The row galleys of Lake Champlain, 1776.
    galley: ['Congress', 'Washington', 'Trumbull', 'Gates', 'Lee'],
    ironclad_battery: ['New Ironsides', 'Galena'],
    steam_frigate: ['Merrimack', 'Minnesota'],
    steam_corvette: ['Hartford', 'Brooklyn'],
    paddle_steamer: ['Mississippi', 'Powhatan'],
    monitor: ['Monitor', 'Passaic'],
  },
  turkey: {
    first_rate: ['Mahmudiye', 'Selimiye', 'Mesudiye'],
    ship_of_line: ['Peyk-i Mesret', 'Burc-u Zafer', 'Fatih Bahri', 'Nusretiye', 'Tevfik-i Rahman'],
    // The frigates of Sinop, 1853.
    sail_frigate: ['Avnillah', 'Fazl-ı Allah', 'Nizamiye', 'Navek-i Bahri'],
    brig: ['Necm-i Fesan', 'Gül-i Sefid', 'Feyz-i Bari', 'Pervaz-ı Bahri', 'Şeref-i Resan', 'Mesir-i Bahri'],
    cutter: ['Tilsim', 'Şahin', 'Doğan'],
    // The carracks of Kemal Reis and their kin.
    galleon: ['Göke', 'Burak Reis', 'Hüma-yı Zafer', 'Mukaddeme-i Hayır'],
    galley: ['Barbaros', 'Turgut Reis', 'Piyale Paşa', 'Kılıç Ali', 'Kemal Reis'],
    ironclad_battery: ['Asar-ı Tevfik', 'Asar-ı Şevket'],
    steam_frigate: ['Ertuğrul', 'Hüdavendigâr'],
    steam_corvette: ['Muin-i Zafer', 'İzzeddin'],
    // A paddle steamer at Sinop.
    paddle_steamer: ['Taif', 'Saik-i Şadi'],
    // A river monitor of the Danube flotilla.
    monitor: ['Feth-ül İslam', 'Hizber'],
  },
};

/** The name of a nation's `n`-th ship (from 1) of class `cls`, or null if it has none. */
export function shipName(nation: string, cls: string, n: number): string | null {
  return SHIP_NAMES[nation]?.[cls]?.[n - 1] ?? null;
}

/** Every name a nation gives ships of class `cls`, in order. */
export function shipNames(nation: string, cls: string): readonly string[] {
  return SHIP_NAMES[nation]?.[cls] ?? [];
}
